/**
 * Vollständigkeits-Bremse — kein halber Satz geht von selbst hinaus. (20.10.2026)
 *
 * Martin, 20.10. 22:00/22:01: sein gesprochener Satz brach mitten ab („… Vor allem wenn das Swoopband“) und wurde so
 * abgeschickt. *„Wie kann es sein, dass ein Satz abbricht? Das System hat abgeschickt, obwohl der Satz nicht fertig war.
 * Das ist ein Bug. Es kann nie abgeschickt werden, wenn der Satz unvollständig ist.“* (Tickets 3173/3283, 2822.)
 *
 * ## Warum hier, und warum zusätzlich
 *
 * Jeder Weg, der OHNE Martins Hand absendet (Stille fest/Auto mit Jevs Urteil, Obergrenzen, Rückfall, Wortstille,
 * Notausgang bei Dauerlärm, Freihand-Pause, Höchstdauer, Satz nach dem Wach-Wort), entschied bisher allein an Zeit und
 * Pegel; die Mitschrift prüfte nur Jev (ein Modell, das bei offenem Ende auch 0,8 gibt) und die Obergrenzen schickten
 * nach 4–9 s Stille IMMER, gleich wie der Text endete (shared/stille-auto.ts `obergrenzeKurzMs`, `autoHoechstensMs`).
 * Diese Bremse sitzt DAHINTER, bei jedem dieser Wege: eine kleine, deterministische Regel am Text, ohne Netz und Modell.
 *
 *   - `offen`    — erkennbar unvollständig: endet auf Bindewort („und“, „dass“, „wenn“, „weil“ …), Füllwort („ähm“),
 *                  „vor allem“/„und zwar“, Artikel/Präposition („dem“, „zum“, „für“, „mit dem“), „,“ „;“ „:“ „–“, oder
 *                  ein Nebensatz ohne Verb („wenn das Swoopband“). Geht NIE von selbst hinaus: die Frist wird verlängert;
 *                  ist danach immer noch nichts gekommen, landet der Text im Eingabefeld (`BREMSE_ZURUECK_HINWEIS`).
 *   - `unsicher` — ohne Satzende („.“ „!“ „?“), sonst unauffällig, mehr als `BREMSE_KURZ_WOERTER` Wörter — oder der
 *                  Nebensatz ohne Verb mit Punkt dahinter. Die Frist wird verlängert, dann geht es hinaus.
 *   - `null`     — fertig: Satzzeichen, Frage, kurzer Befehl („Ja“, „Stopp“, „Danke“, „Mach weiter“), kein Text.
 *
 * Verlängert wird um `BREMSE_FAKTOR` (mindestens `BREMSE_MIN_ZUSATZ_MS`), höchstens bis `BREMSE_HOECHSTENS_MS` —
 * es wartet nie ewig. Ein Notausgang (Dauerlärm, Höchstdauer) wartet nicht noch länger: offen → ins Eingabefeld, unsicher → hinaus.
 *
 * ⚠️ Martins Hand geht IMMER: Senden-Knopf, Taste, Loslassen, das Wort „jetzt senden“ prüfen hier nicht.
 * ⚠️ Ohne Live-Text (nativer Weg ohne Nachhören) gibt es nichts zu prüfen — die Bremse lässt dort alles durch.
 * ⚠️ Fragen („Und?“, „Und wenn?“) sind fertig: wer fragt, wartet auf Antwort.
 * ⚠️ Bewusst NICHT in den harten Listen: abgetrennte Vorsilben („mit“, „an“, „auf“, „aus“, „zu“, „ein“, „vor“ …: „Hör
 *    auf“, „Schalt das Licht ein“), „der/die/das/den“ allein (Pronomen: „Nimm den“), „dann“ („Bis dann“), „denn“,
 *    „als“/„bis“/„seit“/„während“/„damit“ im Nebensatz-Test (auch Präpositionen/Vergleiche: „größer als Peter“),
 *    „weil“/„obwohl“ im Nebensatz-Test (gesprochen oft mit Verb an zweiter Stelle: „weil ich hab keine Zeit“).
 */
import { letztesWort, ohneSchliessende, OFFENE_WOERTER, satzEnde } from "./stille-auto.js";

/** So viele Wörter oder weniger ohne Satzende sind ein kurzer Befehl („Schalt das Licht ein“), kein offener Satz. */
export const BREMSE_KURZ_WOERTER = 4;
/** Um diesen Faktor wird die Frist verlängert, ab der die Regel senden wollte. */
export const BREMSE_FAKTOR = 2;
/** … mindestens um so viel. */
export const BREMSE_MIN_ZUSATZ_MS = 2000;
/** … höchstens bis so viel Stille (wartet die Regel selbst schon länger, etwa feste 30 s: deren Frist). */
export const BREMSE_HOECHSTENS_MS = 12_000;

/** Die Zeile in der Aufnahmekarte, solange die Bremse wartet. */
export const BREMSE_WARTE_HINWEIS = "Satz klingt unfertig – ich warte noch. Sprich weiter oder tippe „Senden“.";
/**
 * Die Zeile bei `unsicher` (nur das Satzzeichen fehlt, z. B. „… Build 117 installiert“): keine Behauptung über den Satz —
 * Martin 22.10. 07:31: „Das ist doch abgeschlossen. Warum reagiert das System mit unfertig?“
 */
export const BREMSE_WARTE_HINWEIS_UNSICHER = "Ich warte kurz, ob noch etwas kommt.";

/** Welche Zeile gehört zum Grad? „unfertig“ nur bei `offen` (Bindewort, Artikel, Komma, Nebensatz ohne Verb). */
export function bremsWarteHinweis(grad: SatzOffen | null): string {
  return grad === "offen" ? BREMSE_WARTE_HINWEIS : BREMSE_WARTE_HINWEIS_UNSICHER;
}
/** Die Meldung, wenn die Bremse den Text ins Eingabefeld legt statt ihn zu senden. */
export const BREMSE_ZURUECK_HINWEIS = "Satz unvollständig – nicht gesendet. Der Text steht im Eingabefeld.";

/**
 * Füllwörter und Bindewörter, nach denen ohne Satzzeichen nie Schluss ist (dazu `OFFENE_WOERTER`).
 * ⚠️ Prüfauflage M1 (20.10.2026): NICHT dabei „aber“, „oder“, „also“, „sprich“, „quasi“, „hm“/„hmm“ — sie stehen auch am
 *    Ende fertiger Rede („Das passt so, oder“, „Ja aber“, „Okay also“, „Das ist es quasi“, „Hmm“). Ohne Satzzeichen sind sie
 *    höchstens `unsicher` (mehr als `BREMSE_KURZ_WOERTER` Wörter).
 */
/**
 * Füllwörter einer Denkpause. Offen auch MIT „.“/„!“ dahinter (26.10.2026, Ticket 3507: setzt die Erkennung nach einem „ähm“ in
 * der Pause einen Punkt, ist „… und dann ähm.“ trotzdem kein Satzende; gemessen ist die Häufigkeit nicht). „?“ bleibt fertig; „hm“/„hmm“ fehlen bewusst (M1 unten).
 */
const FUELLWOERTER: ReadonlySet<string> = new Set(["äh", "ähh", "ähm", "ähhm", "öhm", "ehm"]);

const OFFEN_OHNE_ZEICHEN: ReadonlySet<string> = new Set([
  ...OFFENE_WOERTER,
  ...FUELLWOERTER,
  "beziehungsweise", "wobei", "falls", "sobald", "solange", "sofern",
  // Artikel und Präpositionen, die nie einen Satz beenden (keine Vorsilbe, kein Pronomen).
  "dem", "des", "einem", "eines", "zum", "zur", "beim", "vom", "im", "ins", "am",
  "von", "für", "zwischen", "neben", "hinter", "wegen", "trotz", "gegen", "ohne",
]);
/** Präpositionen vor einem Artikel am Ende („mit dem“, „für die“, „auf den“) — dort fehlt das Nomen. */
const PRAEPOSITIONEN: ReadonlySet<string> = new Set([
  "mit", "für", "von", "zu", "an", "auf", "aus", "bei", "nach", "über", "unter", "vor", "hinter", "neben", "zwischen",
  "durch", "gegen", "ohne", "um", "in", "wegen", "trotz", "seit", "bis",
]);
const ARTIKEL: ReadonlySet<string> = new Set(["der", "die", "das", "den", "dem", "des", "ein", "eine", "einen", "einem", "einer", "eines"]);
/** Wendungen, nach denen immer etwas folgt — auch mit „.“ oder „!“ dahinter. */
const OFFENE_WENDUNGEN: readonly string[] = ["vor allem", "und zwar"];
/** Wendungen, nach denen ohne Satzzeichen etwas folgt („Nimm Äpfel zum Beispiel.“ ist fertig). */
const OFFENE_WENDUNGEN_OHNE_ZEICHEN: readonly string[] = ["zum beispiel", "das heißt", "so wie"];
/** Unterordnende Bindewörter mit Verb am Ende des Nebensatzes (Auswahl, siehe ⚠️ oben). */
const NEBENSATZ_WOERTER: ReadonlySet<string> = new Set([
  "wenn", "dass", "daß", "ob", "nachdem", "bevor", "sodass", "falls", "sobald", "solange", "sofern", "indem", "seitdem", "obgleich",
]);
/**
 * So beginnt der Satzgegenstand eines Nebensatzes („wenn DAS Swoopband“, „ob DER Termin“, „wenn ICH Zeit“) — oder mit einem
 * groß geschriebenen Wort (Name, Nomen). Prüfauflage M1: „wenn möglich heute Abend“ hat keinen (Ellipse, fertig).
 */
const SUBJEKT_ANFANG: ReadonlySet<string> = new Set([
  ...ARTIKEL,
  "ich", "du", "er", "sie", "es", "wir", "ihr", "man", "jemand", "niemand",
  "mein", "meine", "meinen", "meinem", "meiner", "dein", "deine", "deinen", "deinem", "deiner",
  "sein", "seine", "seinen", "seinem", "seiner", "ihre", "ihren", "ihrem", "ihrer",
  "unser", "unsere", "unseren", "unserem", "euer", "eure", "euren", "eurem",
  "kein", "keine", "keinen", "keinem", "dieser", "diese", "dieses", "diesen", "diesem",
]);
/** Höchstens so viele Wörter nach dem Bindewort zählen für „Nebensatz ohne Verb“ (sonst ist es ein langer Nachsatz). */
const NEBENSATZ_HOECHSTENS_WOERTER = 3;

export type SatzOffen = "offen" | "unsicher";

/** Die Wörter (Buchstaben/Ziffern, ein Apostroph im Wort trennt nicht), in Originalschreibung. */
function woerterVon(s: string): string[] {
  return s.replace(/(?<=\p{L})['’ʼ](?=\p{L})/gu, "").split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

/** Endet `klein` (Wörter klein geschrieben) auf eine der Wendungen? */
function endetAuf(klein: readonly string[], wendungen: readonly string[]): boolean {
  const ende = klein.join(" ");
  return wendungen.some((w) => ende === w || ende.endsWith(` ${w}`));
}

/**
 * Ein Nebensatz ohne Verb am Ende („wenn das Swoopband“): im letzten Satzteil steht ein Bindewort aus
 * `NEBENSATZ_WOERTER`, danach 1–3 Wörter, und das letzte ist groß geschrieben (Nomen/Name — das Verb, das im
 * Nebensatz am Ende steht, wäre klein).
 */
function nebensatzOhneVerb(s: string): boolean {
  const teil = s.replace(/[.!?…]+$/u, "").split(/[,;:.!?…–—]/u).at(-1) ?? "";
  const w = woerterVon(teil);
  const letztes = w.at(-1) ?? "";
  if (!/^\p{Lu}/u.test(letztes)) return false;
  for (let i = w.length - 2; i >= 0 && w.length - 1 - i <= NEBENSATZ_HOECHSTENS_WOERTER; i--) {
    if (!NEBENSATZ_WOERTER.has((w[i] ?? "").toLowerCase())) continue;
    const danach = w[i + 1] ?? "";
    return SUBJEKT_ANFANG.has(danach.toLowerCase()) || /^\p{Lu}/u.test(danach);
  }
  return false;
}

/**
 * Ist das Ende dieses Textes offen? `offen` (erkennbar unvollständig), `unsicher` (ohne Satzende) oder `null` (fertig,
 * leer, Frage, kurzer Befehl). Rein und deterministisch — EINE Regel für jeden Weg, der von selbst absendet.
 */
export function satzOffen(text: string): SatzOffen | null {
  const s = ohneSchliessende(text);
  if (!s) return null;
  // Eine Frage ist fertig — auch „Und?“ oder „Und wenn?“.
  if (s.endsWith("?")) return null;
  if (/[,;:\-–—]$/u.test(s)) return "offen";
  const klein = woerterVon(s).map((w) => w.toLowerCase());
  if (klein.length === 0) return null;
  const letztes = letztesWort(s);
  const ende = satzEnde(s);
  // Auch mit „.“/„!“ offen: ein Bindewort vor dem Zeichen („… und.“, `satzEnde` sagt dort schon „keins“), „vor allem.“,
  // seit 26.10.2026 auch ein Füllwort der Denkpause („… ähm.“).
  if (OFFENE_WOERTER.has(letztes) || FUELLWOERTER.has(letztes) || endetAuf(klein, OFFENE_WENDUNGEN)) return "offen";
  if (ende !== "keins") return nebensatzOhneVerb(s) ? "unsicher" : null;
  // Ab hier ohne Satzende.
  const auslassung = /(\.\.|…)$/u.test(s);
  if (!auslassung) {
    if (OFFEN_OHNE_ZEICHEN.has(letztes) || endetAuf(klein, OFFENE_WENDUNGEN_OHNE_ZEICHEN)) return "offen";
    const vorletztes = klein.at(-2) ?? "";
    if (ARTIKEL.has(letztes) && PRAEPOSITIONEN.has(vorletztes)) return "offen";
    if (nebensatzOhneVerb(s)) return "offen";
  }
  return auslassung || klein.length > BREMSE_KURZ_WOERTER ? "unsicher" : null;
}

/**
 * Die verlängerte Frist: `regelAbMs` (die Stille, bei der die Regel senden wollte) × `BREMSE_FAKTOR`, mindestens
 * `+ BREMSE_MIN_ZUSATZ_MS`, höchstens `BREMSE_HOECHSTENS_MS` (oder `regelAbMs`, wenn die Regel selbst länger wartet).
 */
export function bremsFristMs(regelAbMs: number): number {
  const r = Number.isFinite(regelAbMs) ? Math.max(0, regelAbMs) : 0;
  return Math.min(Math.max(r * BREMSE_FAKTOR, r + BREMSE_MIN_ZUSATZ_MS), Math.max(r, BREMSE_HOECHSTENS_MS));
}

/** Was die Bremse aus einem „senden“ der Regel macht. */
export type BremsSchritt = "frei" | "warten" | "zuruecklegen";

export interface BremsLage {
  /** Die Mitschrift jetzt (leer auf dem nativen Weg). */
  text: string;
  /** Die Stille jetzt (ms). */
  stillMs: number;
  /** Bei welcher Stille die Regel für diesen Text und diese Pause zuerst senden wollte (ms). */
  regelAbMs: number;
  /** Will ein Notausgang senden (Dauerlärm, Höchstdauer, Wach-Wort)? Dann wartet die Bremse nicht. */
  notausgang?: boolean;
}

/**
 * Darf, was die Regel senden will, jetzt hinaus? `frei` — ja; `warten` — noch nicht (verlängerte Frist);
 * `zuruecklegen` — nicht senden, den Text ins Eingabefeld (offen nach der Frist, oder ein Notausgang bei offenem Ende).
 */
export function bremsSchritt(l: BremsLage): BremsSchritt {
  const grad = satzOffen(l.text);
  if (grad === null) return "frei";
  // Prüfauflage M2 (20.10.2026, Entscheidung Schmied): ein Notausgang legt NUR „offen“ zurück; „unsicher“ geht hinaus.
  if (l.notausgang === true) return grad === "offen" ? "zuruecklegen" : "frei";
  if (!(l.stillMs >= bremsFristMs(l.regelAbMs))) return "warten";
  return grad === "offen" ? "zuruecklegen" : "frei";
}

/**
 * Ein Notausgang ohne Warten (Dauerlärm, Höchstdauer der Aufnahme): muss der Text ins Eingabefeld statt an die KI?
 * Nur bei offenem Ende (Prüfauflage M2) — nie bei unsicherem oder leerem Text.
 */
export function notausgangZuruecklegen(text: string): boolean {
  return bremsSchritt({ text, stillMs: 0, regelAbMs: 0, notausgang: true }) === "zuruecklegen";
}

/**
 * Die Bremse am ENDGÜLTIGEN Text einer von selbst (Stille) gesendeten Aufnahme (21.10.2026, Vorfall 15:44:53: die Karte prüfte die
 * Live-Mitschrift, sie endete auf „.“ — die Erkennung des Servers endete auf „Und“ und ging hinaus). Die Frist ist dort schon
 * abgelaufen; was jetzt noch `offen` ist, geht nicht an die KI, sondern ins Eingabefeld. `unsicher` geht hinaus (wie Prüfauflage M2).
 *
 * `liveOffen` (21.10.2026, Vorfall 17:25:05): die Karte hielt die Aufnahme an, weil die LIVE-Mitschrift offen endete — legte sie aber
 * selbst ins Eingabefeld, obwohl die Datei-Erkennung des Servers zwei Wörter mehr hatte (Live 29, Datei 31) und den Satz zu Ende führte.
 * Jetzt entscheidet der ENDGÜLTIGE Text: ist er fertig (`satzOffen` = null: Satzzeichen, Frage, kurzer Befehl), geht er hinaus; nur wenn
 * auch er nicht fertig ist (offen ODER unsicher), legt der Server ihn zurück — beide Mitschriften müssen dann „nicht fertig“ sagen.
 */
export function stilleZuruecklegen(text: string, liveOffen = false): boolean {
  const grad = satzOffen(text);
  return liveOffen ? grad !== null : grad === "offen";
}

/**
 * Die Bremse einer Aufnahme: merkt sich, ab welcher Stille die Regel für diesen Text senden wollte, und zählt von dort an
 * der Uhr weiter — solange die Regel ohne Unterbrechung senden will. (Nicht an der Stille des Pegels: die Wortstille
 * sendet auch bei Ton am Pegel, dort fiele die Stille immer wieder auf null, und die Bremse wartete ewig.)
 */
export interface Bremse {
  /** Die Regel will jetzt senden — was gilt? `jetztMs`: eine fortlaufende Uhr (performance.now). */
  pruefe(text: string, stillMs: number, jetztMs: number, notausgang?: boolean): BremsSchritt;
  /** Wartet die Bremse gerade (für den Hinweis in der Karte)? */
  wartet(): boolean;
  /** Der Grad des Textes, auf den die Bremse gerade wartet (`null`: sie wartet nicht). */
  grad(): SatzOffen | null;
  /** Die Regel will (wieder) nicht senden — es wurde weitergesprochen — oder eine neue Aufnahme: vergessen. */
  vergiss(): void;
}

export function neueBremse(): Bremse {
  let ab: { text: string; ms: number; seit: number } | null = null;
  let warte = false;
  let grad: SatzOffen | null = null;
  return {
    pruefe(text, stillMs, jetztMs, notausgang = false) {
      const t = text.trim();
      const still = Number.isFinite(stillMs) ? Math.max(0, stillMs) : 0;
      if (ab?.text !== t) ab = { text: t, ms: still, seit: jetztMs };
      // Seit dem ersten „senden“ für diesen Text vergangen — mindestens die Stille am Pegel jetzt.
      const verstrichen = Math.max(still, ab.ms + Math.max(0, jetztMs - ab.seit));
      const schritt = bremsSchritt({ text: t, stillMs: verstrichen, regelAbMs: ab.ms, notausgang });
      warte = schritt === "warten";
      grad = warte ? satzOffen(t) : null;
      return schritt;
    },
    wartet: () => warte,
    grad: () => grad,
    vergiss() {
      ab = null;
      warte = false;
      grad = null;
    },
  };
}
